import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Trash2, MapPin, Search, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShortlistPage = () => {
  const navigate = useNavigate();
  const { user, token, isLoggedIn, setShowLoginModal } = useAuth();
  const [shortlist, setShortlist] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn) {
      setIsLoading(false);
      return;
    }
    fetchShortlist();
  }, [isLoggedIn, token]);

  const fetchShortlist = async () => {
    try {
      const response = await axios.get(`${API_URL}/shortlist?token=${token}`);
      setShortlist(response.data || []);
    } catch (error) {
      console.error('Failed to fetch shortlist');
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromShortlist = async (venueName) => {
    try {
      await axios.delete(`${API_URL}/shortlist/remove?venue_name=${encodeURIComponent(venueName)}&token=${token}`);
      setShortlist(prev => prev.filter(v => v.venue_name !== venueName));
      toast.success('Removed from shortlist');
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const searchVenue = (venueName) => {
    navigate('/', { state: { searchVenue: venueName } });
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <header className="sticky top-0 z-50 bg-[#FDFCF8]/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-serif text-xl font-medium text-stone-900">My Shortlist</h1>
            <p className="text-sm text-stone-500">{shortlist.length} venues saved</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin mx-auto"></div>
          </div>
        ) : !isLoggedIn ? (
          <div className="text-center py-16 bg-white rounded-xl border border-stone-200">
            <Heart className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-700 mb-2">Login to view your shortlist</h3>
            <p className="text-stone-500 mb-6">Save your favorite venues and access them anytime</p>
            <Button onClick={() => setShowLoginModal(true)} className="bg-stone-900 hover:bg-stone-800">
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </Button>
          </div>
        ) : shortlist.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-stone-200">
            <Heart className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-700 mb-2">No venues shortlisted yet</h3>
            <p className="text-stone-500 mb-6">Search for venues and click "Shortlist" to save them here</p>
            <Button onClick={() => navigate('/')} className="bg-stone-900 hover:bg-stone-800">
              Search Venues
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shortlist.map((item) => (
              <div key={item.id} className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-medium text-stone-900">{item.venue_name}</h3>
                  <button 
                    onClick={() => removeFromShortlist(item.venue_name)}
                    className="text-stone-400 hover:text-red-500 transition-colors"
                    data-testid={`remove-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {item.venue_location && (
                  <p className="text-sm text-stone-500 flex items-center gap-1 mb-4">
                    <MapPin className="w-4 h-4" />
                    {item.venue_location}
                  </p>
                )}
                <p className="text-xs text-stone-400 mb-4">
                  Added {new Date(item.created_at).toLocaleDateString()}
                </p>
                <Button 
                  onClick={() => searchVenue(item.venue_name)}
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                >
                  <Search className="w-4 h-4 mr-2" />
                  View Report
                </Button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ShortlistPage;
